import type { ComponentPropsWithoutRef, ElementType } from 'react';
import { createContext, useContext } from 'react';

import type {
    SwitchGroupProps as HeadlessSwitchGroupProps,
    SwitchProps as HeadlessSwitchProps
} from '@headlessui/react';
import { Switch as HeadlessSwitch } from '@headlessui/react';
import clsx from 'clsx';

import { classNames } from '@/utils/classNames';

export type SwitchSize = 'default' | 'sm';

type DataContextType = Pick<HeadlessSwitchProps<'button'>, 'disabled' | 'checked'> & {
    size?: SwitchSize;
};

const SwitchContext = createContext<DataContextType>(null);

function SwitchWrapper({
    checked = false,
    disabled = false,
    size = 'default',
    className,
    ...restProps
}: HeadlessSwitchGroupProps<'div'> & DataContextType) {
    return (
        <SwitchContext.Provider value={{ checked, disabled, size }}>
            <HeadlessSwitch.Group
                as="div"
                className={clsx('flex items-center space-x-3', className)}
                {...restProps}
            />
        </SwitchContext.Provider>
    );
}

Switch.Wrapper = SwitchWrapper;

type SwitchLabelProps = ComponentPropsWithoutRef<'label'> & {
    as?: ElementType;
    passive?: boolean;
};

function SwitchLabel({ className, ...restProps }: SwitchLabelProps) {
    const { checked, disabled } = useContext(SwitchContext);
    return (
        <HeadlessSwitch.Label
            className={clsx(
                'cursor-pointer text-xs !ring-0',
                {
                    'text-icoBlack dark:text-white': checked,
                    'text-icoGray-400 dark:text-icoDarkMode-wolf': !checked,
                    'cursor-not-allowed': disabled
                },
                className
            )}
            {...restProps}
        />
    );
}

Switch.Label = SwitchLabel;

export type SwitchProps = Omit<HeadlessSwitchProps<'button'>, 'disabled' | 'checked'> & {
    ariaLabel?: string | React.ReactElement;
    dotClassName?: {
        checked?: string;
        unchecked?: string;
        additionalClasses?: string;
    };
};

function Switch({ className, ariaLabel, dotClassName, ...restProps }: SwitchProps) {
    const { checked, disabled, size } = useContext(SwitchContext);
    const isSmall = size === 'sm';

    return (
        <HeadlessSwitch
            data-testid="switch-action"
            disabled={disabled}
            checked={checked}
            className={classNames(
                'relative inline-flex items-center rounded-full',
                {
                    'h-5 w-9': !isSmall,
                    'h-4 w-7': isSmall,
                    'bg-icoBlue-100 dark:bg-icoBlue/20': checked,
                    'bg-icoGray-300 dark:bg-white/20': !checked,
                    'cursor-not-allowed': disabled
                },
                className
            )}
            {...restProps}>
            <span className="sr-only">{ariaLabel}</span>
            <span
                className={classNames(
                    'inline-block transform rounded-full transition duration-200 ease-in-out',
                    {
                        'h-3 w-3': !isSmall,
                        'h-2.5 w-2.5': isSmall,
                        [dotClassName?.checked ??
                        (isSmall ? 'translate-x-3.5 bg-icoBlue' : 'translate-x-5 bg-icoBlue')]:
                            checked,
                        [dotClassName?.unchecked ??
                        'translate-x-1 bg-icoGray-400 dark:bg-icoDarkMode-wolf']: !checked
                    },
                    dotClassName?.additionalClasses
                )}
            />
        </HeadlessSwitch>
    );
}

export default Switch;
